import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  Skeleton,
} from '@mui/material';
import {
  FitnessCenter as FitnessCenterIcon,
  CalendarToday as CalendarIcon,
  Layers as LayersIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import AppBarContext from 'context/AppBarContext';
import performanceSlice from 'store/modules/performance';
import { roles } from 'common/constants/options';
import DynamicExerciseGroup from './DynamicExerciseGroup';
import CreatePerformanceButton from './CreatePerformanceButton';

function PerformanceHistory({ clientId = null }) {
  const dispatch = useDispatch();
  const { t } = useTranslation(['routes', 'performances']);
  const pageTitle = t('performance.myPerformance', { ns: 'routes' });
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const authUserId          = useSelector((state) => state.auth.user?.id);
  const role                = useSelector((state) => state.auth.user?.role);
  const viewClient          = useSelector((state) => state.client.data.viewClient);
  const targetUserId        = clientId ?? authUserId;
  const isAdminView         = !!clientId;
  const groups              = useSelector((state) => state.performance.data.dynamicPerformancesByExercise);
  const listStatus          = useSelector((state) => state.performance.status.listDynamic);

  // El cliente cargado (con membership_status) para el botón de crear rendimiento; se valida el id
  // para no usar un cliente obsoleto de una vista anterior.
  const adminClient = isAdminView && viewClient && String(viewClient.id) === String(clientId) ? viewClient : null;
  const canCreatePerformance = isAdminView && (role === roles.administrator || role === roles.trainer);

  useEffect(() => {
    if (targetUserId) dispatch(performanceSlice.actions.getDynamicByExercise(targetUserId));
  }, [targetUserId, dispatch]);

  useEffect(() => {
    if (!isAdminView) {
      setAppBarButton(null);
      setAppBarTitle(pageTitle);
    }
  }, [isAdminView, pageTitle, setAppBarButton, setAppBarTitle]);

  // ── Estadísticas de resumen ──────────────────────────────────────────────
  const totalSessions   = groups.reduce((s, g) => s + g.total_count, 0);
  const totalExercises  = groups.length;
  const lastActivity    = (() => {
    const allDates = groups.flatMap((g) =>
      g.performances.map((p) => p.period_start_date)
    );
    if (!allDates.length) return null;
    const latest = allDates.sort((a, b) => new Date(b) - new Date(a))[0];
    try { return format(parseISO(latest), 'd MMM yyyy', { locale: es }); }
    catch { return null; }
  })();

  const loading = listStatus === 'loading';

  const summaryCards = [
    {
      icon: FitnessCenterIcon,
      value: totalSessions,
      label: t('performance.totalSessions', { ns: 'routes', defaultValue: 'Sesiones totales' }),
    },
    {
      icon: LayersIcon,
      value: totalExercises,
      label: t('performance.exerciseTypes', { ns: 'routes', defaultValue: 'Tipos de ejercicio' }),
    },
    {
      icon: CalendarIcon,
      value: lastActivity || '—',
      label: t('performance.lastActivity', { ns: 'routes', defaultValue: 'Última actividad' }),
      small: !!lastActivity,
    },
  ];

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Container maxWidth="xl" sx={{ py: 3 }}>

        {/* ── Acción: crear rendimiento (entrenador/admin viendo a un cliente) ── */}
        {canCreatePerformance && adminClient && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <CreatePerformanceButton client={adminClient} />
          </Stack>
        )}

        {/* ── Cards de resumen ── */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <Grid item xs={12} sm={4} key={card.label}>
                {loading ? (
                  <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
                ) : (
                  <Box
                    sx={{
                      height: '100%',
                      p: 2.5,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'hairline.main',
                      backgroundColor: 'surface.raised',
                      boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow)',
                      transition: 'transform 0.25s cubic-bezier(0.32,0.72,0,1), border-color 0.25s cubic-bezier(0.32,0.72,0,1)',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        borderColor: 'hairline.strong',
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                      <Icon sx={{ color: 'gold.main', fontSize: 22 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                        {card.label}
                      </Typography>
                    </Stack>
                    <Typography
                      variant={card.small ? 'h6' : 'h4'}
                      sx={{ fontWeight: 800, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {card.value}
                    </Typography>
                  </Box>
                )}
              </Grid>
            );
          })}
        </Grid>

        {/* ── Lista de ejercicios ── */}
        {loading && (
          <Stack spacing={2}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={72} sx={{ borderRadius: 2 }} />
            ))}
          </Stack>
        )}
        {!loading && groups.length === 0 && (
          /* ── Estado vacío ── */
          <Box
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              backgroundColor: 'surface.content',
              border: '1px solid',
              borderColor: 'hairline.main',
              borderRadius: 3,
            }}
          >
            <FitnessCenterIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
              {t('performance.emptyTitle', { ns: 'routes', defaultValue: 'Aún no tienes rendimientos' })}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 380, mx: 'auto' }}>
              {t('performance.emptyBody', {
                ns: 'routes',
                defaultValue:
                  'Tu entrenador registrará tus ejercicios dinámicos aquí. Una vez que tengas sesiones, podrás ver tu progreso y tu posición en el ranking de cada ejercicio.'
              })}
            </Typography>
          </Box>
        )}
        {!loading && groups.length > 0 && (
          /* ── Grupos de ejercicio ── */
          <Box>
            {groups.map((group) => (
              <DynamicExerciseGroup
                key={group.exercise.id}
                group={group}
                profileUserId={targetUserId}
              />
            ))}
          </Box>
        )}
      </Container>
    </>
  );
}

PerformanceHistory.propTypes = {
  clientId: PropTypes.string,
};

export default PerformanceHistory;
